import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CustomizedPagination } from "../../components/CustomizedTable/CustomizedPagination";
import { LoadingOverlay } from "../../components/LoadingOverlay/LoadingOverlay";
import { StyledSelect } from "../../components/StyledSelect/StyledSelect";
import { TableSearch } from "../../components/TableSearch";
import { useApplications } from "../../hooks/reactQueryHooks/useApplications";
import { useKeys } from "../../hooks/reactQueryHooks/useKeys";
import type { FetchAllMentorResponse } from "../../hooks/reactQueryHooks/useMentors";
import { useMentors } from "../../hooks/reactQueryHooks/useMentors";
import { useQueryParams } from "../../hooks/useQueryParams";
import { MentorCard } from "./components";

interface MentorPageProps {
  organizationId: string;
}

export function MentorPage({ organizationId }: MentorPageProps) {
  // # region inbuilt hooks
  const navigate = useNavigate();

  const { queryValue: pageQuery } = useQueryParams({
    queryStr: "p",
  });

  const { queryValue: searchString } = useQueryParams({
    queryStr: "s",
  });

  const [apiKey, setApiKey] = useState<
    | {
        value: string;
        label: string;
      }
    | undefined
  >(undefined);

  // # endregion

  // # region react query hooks
  const { data: keysList, isLoading: isKeysLoading } = useKeys({
    organizationId,
    type: "proxy",
    pageSize: 100,
  });

  const { data: applicationsList, isLoading: isAppsLoading } =
    useApplications(organizationId);

  // # endregion

  const keysOptions = useMemo(() => {
    if (!keysList?.items || !applicationsList) return [];
    const formattedKeys = keysList.items
      .map(key => ({
        value: key.key || "",
        label: `${key.name} (${
          applicationsList?.find(
            application => application.id === key.applicationId,
          )?.name
        })`,
      }))
      .filter(key => !key.label.includes("undefined"));
    setApiKey(formattedKeys[0]);
    return formattedKeys;
  }, [keysList, applicationsList]);

  const {
    data: mentorsData,
    isLoading,
    isFetching,
  } = useMentors({
    pageIndex: pageQuery ? Number(pageQuery) - 1 : 0,
    pageSize: 12,
    search: searchString,
  });

  return (
    <div className="container-inner-wrapper">
      <div className="custom-header-wrapper mentor-control-container mb-2">
        <div className="align-items-center position-relative">
          <TableSearch
            label="Search all mentors"
            placeholder="Type to filter..."
          />
        </div>
        {/* <div className="align-items-center position-relative">
          <div className="card-title wrap-next-line mb-md-0 mb-8">
            <StyledSelect
              options={keysOptions}
              isDisabled={false}
              isLoading={isKeysLoading || isAppsLoading}
              isSearchable
              onChange={setApiKey}
              value={apiKey}
              placeholder="Select API key"
            />
            <div className="short-explanation w-100">Select an API key.</div>
          </div>
        </div> */}
      </div>
      <div className="container">
        {((mentorsData as FetchAllMentorResponse)?.items ?? mentorsData)
          ?.length === 0 ? (
          <div className="empty-view">
            <div className="text-center pt-10">No mentors found.</div>
          </div>
        ) : (
          ((mentorsData as FetchAllMentorResponse)?.items ?? mentorsData)?.map(
            mentor => (
              <div key={mentor.mentorId}>
                <MentorCard
                  profile={mentor}
                  onCardClick={() => {
                    const uriPath = `/session/${mentor.mentorId}`;
                    navigate(uriPath, {
                      state: {
                        apiKey: apiKey?.value || "",
                        mentorId: mentor.mentorId,
                      },
                    });
                  }}
                />
              </div>
            ),
          )
        )}
      </div>
      {(mentorsData as FetchAllMentorResponse)?.pageSize && (
        <div className="mt-5 d-flex justify-content-end">
          <CustomizedPagination
            itemsPerPage={(mentorsData as FetchAllMentorResponse)?.pageSize}
            totalItems={(mentorsData as FetchAllMentorResponse)?.filteredCount}
            pageIndex={(mentorsData as FetchAllMentorResponse)?.pageIndex}
          />
        </div>
      )}

      {(isLoading || isFetching) && (
        <div className="spinner-container">
          <LoadingOverlay />
        </div>
      )}
    </div>
  );
}
